import { etiquetaDelFiltroDeMatch } from "@/pages/sellers/products/filtroDeMatch";
import { ORDER_STATUS_GROUPS } from "@/utilities/order-shipment-status";
import {
  getDeliveryMethods,
  getDeliveryStatuses,
  getPaymentMethods,
  getPaymentStatuses,
} from "@/pages/orders/ordersFilters";

export const getFieldLabel = (t: any, field: string): string => {
  const labels: Record<string, string> = {
    linked_store: t("filter.select.labelStores"),
    name: t("products.import.form.label.name"),
    state: t("filter.select.labelStatus"),
    featured: t("filter.products.labelFeatured"),
    "variations.enabled": t("filter.products.labelStatus"),
    manufacturer: t("filter.products.labelBrand"),
    "variations.mainCatalog.sku": t("filter.products.labelManufacturerSku"),
    sku: "SKU",
    operators: t("filter.operators.title"),
    customers: t("filter.customers.title"),
    price: t("products.show.tablePrice"),
    "product.status": t("filter.products.labelStatusCatalog"),
    matchScore: t("filter.products.labelMatch"),
    importId: t("filter.products.labelImport"),
    "quoteSellers.state": t("posts.fields.status.title"),
    region: t("create.seller.labelState"),
    city: t("create.seller.labelCity"),
    "customers.userData.firstname": t("menu.label.contacts"),
    "quoteSellers.sellerCode": "Seller",
    sellerStatus: "Seller status",
    "storeLinkedSellers.active": t("filter.products.labelStatusPausedByStore"),
    "storeLinkedSellerRequests.active": t("filter.products.labelStatusPausedBySeller"),
    storeStatus: "Store status",
    linkStatus: "Link status",
    requestStatus: "Request status",
    "contactGroupRelation.contactGroup.name": "Contact group",
    "participations.contactId": "Relación",
    "participations.userId": t("filter.participant.me", "Soy participante"),
    "participations.email": "Contacto",
    "user.email": "Contacto",
    "user.company": "Compañía",
    sellerCode: "Seller",
    userId: "Contact",
    // Los de Pedidos, con los mismos nombres que el formulario de filtros.
    "payments.status": t("common.payment.status"),
    "shipments.status": t("orders.table.deliveryStatus"),
    "payments.paymentMethod": t("common.payment.method"),
    "shipments.deliveryMethod": t("orders.label.deliverymethod"),
    status: t("orders.table.status"),
    customerId: "Contact",
    archived: "Status",
    createdAt: t("filter.calendar.lastCreate"),
    calendar_created: t("filter.calendar.lastCreate"),
    updatedAt: t("filter.calendar.lastUpdate"),
    calendar: t("filter.calendar.lastUpdate"),
  };

  return labels[field] || field;
};

export const fieldClearers: Record<string, string[]> = {
  price: ["price_product_from", "price_product_to"],
  "quoteSellers.total": ["price_product_from", "price_product_to"],
  updatedAt: ["calendar"],
  createdAt: ["calendar_created"],
  manufacturer: ["brands_product"],
  name: ["name_product"],
  "variations.enabled": ["status_product"],
  "variations.mainCatalog.sku": ["manufacture_sku"],
  sku: ["catalog_sku"],
  featured: ["featured_product"],
  "product.status": ["status"],
  matchScore: ["match_tramo", "match_desde", "match_hasta"],
  importId: [],
  "quoteSellers.state": ["status"],
  region: ["region"],
  city: ["city"],
  "customers.userData.firstname": ["contacts"],
  "quoteSellers.sellerCode": ["sellerCode"],
  sellerStatus: ["sellerStatus"],
  storeStatus: ["storeStatus"],
  linkStatus: ["linkStatus"],
  requestStatus: ["requestStatus"],
   "contactGroupRelation.contactGroup.name": ["contactGroups"],
   "participations.contactId": ["contactRelation"],
   "participations.userId": ["participant_toggle"],
   "participations.email": ["contactRelation"],
   "user.email": ["contactRelation"],
   "user.company": ["company"],
   "payments.status": ["paymentStatus"],
   "shipments.status": ["deliveryStatus"],
   "payments.paymentMethod": ["paymentMethod"],
   "shipments.deliveryMethod": ["deliveryMethod"],
   archived: ["archived_status"],
};

export const statusMap = (t: any) => ({
  1: t("filter.products.labelStatusActive"),
  0: t("filter.products.labelStatusDesactive"),
  true: t("filter.products.labelStatusActive"),
  false: t("filter.products.labelStatusDesactive"),
  enabled: t("filter.products.labelStatusActive"),
  disabled: t("filter.products.labelStatusDesactive"),
  buy_on: t("quotes.status.Bought"),
  bought_assisted: t("quotes.status.BoughtAssisted"),
  buy_off: t("quotes.status.BoughtOff"),
  lost: t("quotes.status.Lost"),
  archived: t("quotes.status.Archived"),
  follow_up: t("quotes.status.Following"),
  follow: t("quotes.status.Following"),
  open: t("quotes.status.Open"),
  client: t("quotes.status.Customer"),
  customer: t("quotes.status.Customer"),
  quoting: t("quotes.status.Quoting"),
  store: t("quotes.status.Quoting"),
});

export const linkedMap = (t: any) => ({
  linked: t("filter.products.optionLinked"),
  unlinked: t("filter.products.optionUnlinked"),
  catalogation_requested: t("filter.products.optionCatalogation"),
});

export const excludedFields = [
  "search",
  "store.code",
  "contacts.storeCode",
  "storeCode"
];

export const getDisplayValue = (
  t: any,
  field: string,
  value: any,
  storesSelectProps: any,
): React.ReactNode => {
  const sMap = statusMap(t);
  const paymentMethods = getPaymentMethods(t);
  const deliveryMethods = getDeliveryMethods(t);
  const deliveryStatuses = getDeliveryStatuses(t);
  const paymentStatuses = getPaymentStatuses(t);

  // El tramo del filtro de match (xcons-docs#624).
  if (field === "matchScore") return etiquetaDelFiltroDeMatch(t, value);
  if (field === "importId") return `#${value}`;

  if (field === "archived") {
    if (value === "all") return t("filter.calendar.all");
    return t("quotes.status.Archived");
  }

  if (field === "linked_store" && storesSelectProps?.options) {
    return (
      storesSelectProps.options.find((o: any) => o.value === value)?.label ??
      value
    );
  }

  if (field === "quoteSellers.state" && Array.isArray(value)) {
    // "Store" manda ["quoting", "store"] (operator "in"): las dos resuelven al
    // mismo label, así que se dedupea en vez de mostrar "Store, Store".
    const labels = value.map(
      (v) => sMap[v as keyof typeof sMap] ?? v,
    );
    return Array.from(new Set(labels)).join(", ");
  }

  // Destacado es Sí / No: el mapa compartido dice Activo / Inactivo (xcons-docs#609).
  if (field === "featured" && (String(value) === "1" || String(value) === "0")) {
    return String(value) === "1"
      ? t("filter.products.featuredYes")
      : t("filter.products.featuredNo");
  }

  if (
    ["quoteSellers.state", "featured", "variations.enabled"].includes(field) &&
    sMap[value as keyof typeof sMap] !== undefined
  ) {
    return sMap[value as keyof typeof sMap];
  }

  // El `status` de Pedidos llega como el grupo entero (Abierto manda diez estados): se muestra el
  // nombre del grupo, no los valores pegados (xcons-docs#480).
  if (field === "status" && Array.isArray(value)) {
    const groupLabels = {
      open: t("quotes.status.Open"),
      complete: t("orders.status.complete"),
      canceled: t("orders.status.cancel"),
    };
    const group = (Object.keys(ORDER_STATUS_GROUPS) as (keyof typeof ORDER_STATUS_GROUPS)[]).find(
      (key) =>
        ORDER_STATUS_GROUPS[key].length === value.length &&
        ORDER_STATUS_GROUPS[key].every((s) => value.includes(s)),
    );
    return group ? groupLabels[group] : value.join(", ");
  }

  if (field === "payments.paymentMethod") {
    if (Array.isArray(value)) {
      return value
        .map(
          (v) =>
            paymentMethods.find((pm) => pm.value === v)?.label ?? v
        )
        .join(", ");
    }
    const found = paymentMethods.find((pm) => pm.value === value);
    return found ? found.label : value;
  }

  if (field === "shipments.deliveryMethod") {
    if (Array.isArray(value)) {
      return value
        .map(
          (v) =>
            deliveryMethods.find((pm) => pm.value === v)?.label ?? v
        )
        .join(", ");
    }
    const found = deliveryMethods.find((pm) => pm.value === value);
    return found ? found.label : value;
  }

  if (field === "payments.status") {
    const etiqueta = (v: string) =>
      paymentStatuses.find((ps) => ps.value === v)?.label ?? v;
    return Array.isArray(value)
      ? value.map(etiqueta).join(", ")
      : etiqueta(value);
  }

  if (field === "shipments.status") {
    if (Array.isArray(value)) {
      return value
        .map(
          (v) =>
            deliveryStatuses.find((pm) => pm.value === v)?.label ?? v
        )
        .join(", ");
    }
    const found = deliveryStatuses.find((pm) => pm.value === value);
    return found ? found.label : value;
  }

  if (value === 1 || value === true) {
    return t("filter.products.labelStatusActive");
  }
  if (value === 0 || value === false) {
    return t("filter.products.labelStatusDesactive");
  }

  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return value;
};


export const isBooleanLabelOnlyField = (field: string): boolean => {
  return ["storeLinkedSellers.active", "storeLinkedSellerRequests.active", "sellerStatus", "storeStatus"].includes(field);
};
